import React, { useContext, useMemo, useState } from 'react';
import {
  Card, IconButton, Text, Badge, Menu, Divider, Button, Dialog, Portal, TextInput,
} from 'react-native-paper';
import { Image, Platform } from 'react-native';
import { View } from 'native-base';
import * as ImagePicker from 'expo-image-picker';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import { AuthContext } from '../../context/AuthContext';
import { FindOneUserQuery } from '../../gql/user/query';
import { GET_CURRENT_SEASON } from '../../gql/season/query';
import { styles } from '../Common.style';
import ProfileTabs from './ProfileTabs';
import { UpdateProfileMutation } from '../../gql/profile/mutation';
import { computeCategorie, computeWeightBasedOnCategorie } from '../../utils/utils';
import { ModalContext } from '../../context/ModalContext';
import WeightTrackerModal from '../../modals/ManageWeights';
import InformationModal from '../../modals/Information/InformationModal';
import ConfirmationModal from '../../modals/Confirmation/ConfirmationModal';
import CreateAchievementModal from '../../modals/ManageAchievements/CreateAchievementModal';
import { DemoteCompetitorToActive, PromoteOneUserToCompetitor, UpdateOneUser } from '../../gql/user/mutation';
import { FindManyEvents } from '../../gql/event/query';
import { ROLE_NAMES, SCREEN_NAMES } from '../../utils/constant';
import LoadingIndicator from '../../components/commons/LoadingIndicator';

function Profile() {
  const route = useRoute();
  const navigation = useNavigation();
  const { authState, isSuperAdmin } = useContext(AuthContext);
  const {
    displayWeightModal,
    toggleWeightModal,
    displayInformationModal,
    toggleInformationModal,
    displayConfirmationModal,
    toggleConfirmationModal,
    displayCreateAchievementModal,
  } = useContext(ModalContext);

  const [menuVisible, setMenuVisible] = useState(false);
  const openMenu = () => setMenuVisible(true);
  const closeMenu = () => setMenuVisible(false);

  const {
    data: oneUserQuery,
    loading: oneUserQueryLoading,
    error: oneUserQueryError,
    refetch,
  } = useQuery(FindOneUserQuery, {
    variables: {
      email: route.params?.email ?? authState.email,
    },
  });
  const { data: dataSeason } = useQuery(GET_CURRENT_SEASON);
  const user = oneUserQuery?.findOneUser;

  const [
    updateProfileForUser,
  ] = useMutation(UpdateProfileMutation);

  const [updateOneUser] = useMutation(UpdateOneUser);

  const [promoteOneUserToCompetitor] = useMutation(PromoteOneUserToCompetitor);
  const [demoteCompetitorToActive] = useMutation(DemoteCompetitorToActive);
  const [demoteDialogVisible, setDemoteDialogVisible] = useState(false);
  const [demoteReason, setDemoteReason] = useState('');

  const handleDemote = async () => {
    setDemoteDialogVisible(false);
    await demoteCompetitorToActive({
      variables: { email: user.email, reason: demoteReason.trim() || null },
      refetchQueries: [FindOneUserQuery, 'findOneUser', FindManyEvents, 'findManyEvents'],
    });
    setDemoteReason('');
  };

  const saveProfilePictureCallback = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 5],
      quality: 1,
      base64: true,
    });
    if (!result.canceled) {
      const picture = Platform.OS === 'web' ? result.assets[0].uri : `data:${result.assets[0].mimeType};base64,${result.assets[0].base64}`;
      if (authState.role === ROLE_NAMES.SUPERADMIN) {
        await updateOneUser({
          variables: {
            updateOneUserId: user.id,
            profile: {
              picture,
            },
          },
        });
      } else {
        // upload asset using gql
        await updateProfileForUser({
          variables: {
            picture,
          },
        });
      }
      refetch();
    }
  };

  // Computed values
  const categories = useMemo(() => {
    if (dataSeason?.findCurrentSeason && user?.profile?.dateOfBirth) {
      return computeCategorie(new Date(user.profile.dateOfBirth), dataSeason?.findCurrentSeason);
    }
    return undefined;
  }, [user, dataSeason]);

  const isMyProfile = authState.email === user?.email;

  return (
    <View style={styles.containerFlex}>
      {oneUserQueryLoading && <LoadingIndicator text="Chargement du profil utilisateur..." />}
      {oneUserQueryError && (
        <View style={styles.Middle}>
          <Text>{oneUserQueryError.message}</Text>
        </View>
      )}
      {user ? (
        <>
          {displayWeightModal.display && <WeightTrackerModal />}
          {displayInformationModal.display && <InformationModal />}
          {displayConfirmationModal.display && <ConfirmationModal />}
          <Portal>
            <Dialog visible={demoteDialogVisible} onDismiss={() => setDemoteDialogVisible(false)}>
              <Dialog.Title>Retirer le statut compétiteur</Dialog.Title>
              <Dialog.Content>
                <Text>
                  L&apos;utilisateur repasse actif. Toutes ses inscriptions aux événements à venir
                  et ses places de trajet sont supprimées immédiatement.
                </Text>
                <TextInput
                  label="Motif (optionnel, envoyé à l'utilisateur)"
                  value={demoteReason}
                  onChangeText={setDemoteReason}
                  multiline
                  style={{ marginTop: 12 }}
                />
              </Dialog.Content>
              <Dialog.Actions>
                <Button onPress={() => setDemoteDialogVisible(false)}>Annuler</Button>
                <Button textColor="red" onPress={handleDemote}>Confirmer</Button>
              </Dialog.Actions>
            </Dialog>
          </Portal>
          {displayCreateAchievementModal.display && <CreateAchievementModal />}
          <Card>
            {user.profile?.pictureThumbnail ? (
              <Image
                style={{
                  alignSelf: 'center', // Changed from alignItems to alignSelf
                  backgroundColor: 'transparent',
                  width: 138,
                  height: 177,
                  resizeMode: 'cover',
                }}
                source={{ uri: user.profile.pictureThumbnail }}
              />
            ) : null}
            <Card.Actions>
              {user.status === 'COMPETITOR' && <IconButton icon="karate" />}

              <Menu
                visible={menuVisible}
                onDismiss={closeMenu}
                anchor={<Button mode="contained" onPress={openMenu} icon="trophy-variant-outline">Catégories</Button>}
              >
                <Menu.Item title="Catégories Nationales" disabled />
                {categories?.national?.map((category) => {
                  const weightCategory = user.profile?.weights.length > 0
                    ? computeWeightBasedOnCategorie(
                      user.profile.weights[user.profile.weights.length - 1].value,
                      user.profile.gender,
                      category,
                    ) : '';
                  return (
                    <Menu.Item
                      key={`nat-menu-${category}`}
                      title={`${category} ${weightCategory}kg`}
                    />
                  );
                })}
                <Divider />
                <Menu.Item title="Catégories Internationales" disabled />
                {categories?.international?.map((category) => {
                  const weightCategory = user.profile?.weights.length > 0
                    ? computeWeightBasedOnCategorie(
                      user.profile.weights[user.profile.weights.length - 1].value,
                      user.profile.gender,
                      category,
                    ) : '';
                  return (
                    <Menu.Item
                      key={`int-menu-${category}`}
                      title={`${category} ${weightCategory}kg`}
                    />
                  );
                })}
              </Menu>

              <View>
                <Badge style={{ position: 'absolute', top: 5, right: 5 }}>+</Badge>
                <IconButton
                  icon="weight-kilogram"
                  size={20}
                  onPress={() => (isMyProfile ? toggleWeightModal() : toggleWeightModal({ profileId: user.profile.id }))}
                />
              </View>
              {isSuperAdmin && authState.email !== user.email && user.status === 'REVIEW_PENDING'
                ? (
                  <IconButton
                    icon="checkbox-marked-circle-plus-outline"
                    size={20}
                    onPress={() => toggleConfirmationModal({
                      title: 'Confirmation',
                      message: 'Voulez-vous vraiment valider l\'utilisateur ? En faisant cela, l\'utilisateur deviendra un compétiteur et pourra s\'inscrire aux compétitions.',
                      handleSubmit: async () => {
                        await promoteOneUserToCompetitor({
                          variables: {
                            email: user.email,
                          },
                          refetchQueries: [
                            FindOneUserQuery, // DocumentNode object parsed with gql
                            'findOneUser', // Query name
                          ],
                        });
                      },
                    })}
                  />
                ) : null}
              {isSuperAdmin && authState.email !== user.email && user.status === 'COMPETITOR'
                ? (
                  <IconButton
                    icon="account-arrow-down-outline"
                    size={20}
                    onPress={() => setDemoteDialogVisible(true)}
                  />
                ) : null}
              {authState.email === user.email && user.status !== 'COMPETITOR' ? (
                <IconButton
                  icon={user.status === 'REVIEW_PENDING' ? 'progress-clock' : 'message-alert'}
                  size={20}
                  onPress={() => toggleInformationModal({
                    title: 'Information importante',
                    message: `Afin de s'inscrire aux compétitions, il est nécessaire de :
- ajouter l'ensemble des documents requis depuis l'onglet "Documents"
- faire valider la cotisation
- faire valider la caution

Une fois ces trois conditions remplies, un bouton "Soumettre pour validation" sera disponible dans l'onglet "Documents". Un administrateur vérifiera ensuite le dossier pour valider le statut compétiteur.
                  `,
                  })}
                />
              ) : null}
              <IconButton
                icon="image-edit"
                size={20}
                onPress={saveProfilePictureCallback}
              />
              <IconButton
                icon="delete-forever"
                size={20}
                iconColor="red"
                onPress={() => navigation.navigate(SCREEN_NAMES.DELETE_ACCOUNT)}
              />
            </Card.Actions>
          </Card>
          <ProfileTabs user={user} disabled={!isSuperAdmin} />
        </>
      ) : null}
    </View>
  );
}

export default Profile;
